import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { api, apiUpload, fileUrl } from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import { Images, Plus, Trash2, Play, Image as ImageIcon, Film, Folder, FolderOpen, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

import { swallow } from "@/lib/log";
import FavPinButtons from "@/components/FavPinButtons";
const TABS = [
  { key: "all",    label: "All",    icon: Images },
  { key: "photos", label: "Photos", icon: ImageIcon },
  { key: "videos", label: "Videos", icon: Film },
];

function fmtSize(n) {
  if (!n) return "";
  const mb = n / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(n / 1024)} KB`;
}

export default function Media() {
  const [sp, setSp] = useSearchParams();
  const initialTab = ["all","photos","videos"].includes(sp.get("tab")) ? sp.get("tab") : "all";
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [tab, setTab] = useState(initialTab);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  // Folders (media_folders)
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null); // folder_id | 'unfiled' | null(all)

  // Folder picker dialog for uploads
  const [folderPicker, setFolderPicker] = useState({ open: false, kind: null, file: null });
  const [pickerChoice, setPickerChoice] = useState({ mode: "none", id: "", name: "" });

  useEffect(() => {
    const t = sp.get("tab");
    if (t && ["all","photos","videos"].includes(t)) setTab(t);
  }, [sp]);

  const selectTab = (key) => {
    setTab(key);
    const next = new URLSearchParams(sp);
    if (key === "all") next.delete("tab"); else next.set("tab", key);
    setSp(next, { replace: true });
  };

  const load = async () => {
    try {
      const [p, v, f] = await Promise.all([
        api.get("/photos"),
        api.get("/videos"),
        api.get("/folders").catch(() => ({ data: [] })),
      ]);
      setPhotos(p.data || []);
      setVideos(v.data || []);
      setFolders(f.data || []);
    } catch (e) { swallow("Media")(e); }
  };
  useEffect(() => { load(); }, []);

  const onPickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input first so the same file can be reselected later
    e.target.value = "";
    setPickerChoice({ mode: "none", id: "", name: "" });
    setFolderPicker({ open: true, kind: "photo", file });
  };

  const onPickVideo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setPickerChoice({ mode: "none", id: "", name: "" });
    setFolderPicker({ open: true, kind: "video", file });
  };

  const doUpload = async (fileTitle) => {
    const { kind, file } = folderPicker;
    if (!file) return;
    // Snapshot picker choice BEFORE we clear it so we can build an optimistic
    // folder entry after the upload succeeds.
    const chosenMode = pickerChoice.mode;
    const chosenName = pickerChoice.name.trim();
    setFolderPicker({ open: false, kind: null, file: null });
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", fileTitle || file.name);
    if (chosenMode === "existing" && pickerChoice.id) fd.append("folder_id", pickerChoice.id);
    if (chosenMode === "new" && chosenName) fd.append("folder_name", chosenName);
    try {
      const path = kind === "photo" ? "/photos" : "/videos";
      const res = await apiUpload(path, fd);
      const savedFolderId = res?.folder_id || null;
      // Optimistic add first — this guarantees the just-uploaded item is
      // visible in the target folder view even if the SW returns a stale
      // /api/photos or /api/videos snapshot on the reload below.
      if (res && res.id) {
        if (kind === "photo") setPhotos((arr) => (arr.some((x) => x.id === res.id) ? arr : [res, ...arr]));
        else setVideos((arr) => (arr.some((x) => x.id === res.id) ? arr : [res, ...arr]));
      }
      // If this upload created a brand-new folder we don't have client-side
      // yet, optimistically stitch it into the folders list so the chip appears
      // instantly with the correct label. We then background-refresh from the
      // server to reconcile counts (folder scope, aggregate totals, etc).
      if (savedFolderId && !folders.some((f) => f.id === savedFolderId)) {
        const optimisticFolder = {
          id: savedFolderId,
          name: chosenMode === "new" && chosenName ? chosenName : "Folder",
          scope: kind === "photo" ? "photos" : "videos",
          counts: { photos: kind === "photo" ? 1 : 0, videos: kind === "video" ? 1 : 0, documents: 0, total: 1 },
        };
        setFolders((prev) => (prev.some((f) => f.id === savedFolderId) ? prev : [optimisticFolder, ...prev]));
        // Background reconciliation of folders list — replaces the stub with
        // the canonical server-side row (scope, real counts, timestamps).
        api.get("/folders").then(({ data }) => setFolders(data || [])).catch(() => { /* noop */ });
      }
      // Snap the folder + tab so the item is immediately in the visible slice.
      // NB: we do this AFTER inserting the optimistic folder so activeFolder
      // resolves to a chip that actually exists in state (avoids empty view).
      if (savedFolderId) setActiveFolder(savedFolderId);
      else setActiveFolder(null);
      selectTab(kind === "photo" ? "photos" : "videos");
      const label = chosenMode !== "none" ? " to folder" : "";
      toast.success(`${kind === "photo" ? "Photo" : "Video"} saved${label}`);
      // Background reconciliation — canonical order, updated counts, etc.
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const del = (item) => {
    // Delete-with-undo (audit #6). Item is optimistically removed from local state
    // and confirmed after the toast timer elapses.
    if (item._kind === "photo") setPhotos((arr) => arr.filter((x) => x.id !== item.id));
    else setVideos((arr) => arr.filter((x) => x.id !== item.id));

    let undone = false;
    toast(`Delete ${item._kind}?`, {
      description: "Undo within 5 seconds",
      duration: 5000,
      action: { label: "Undo", onClick: () => { undone = true; load(); } },
    });
    setTimeout(async () => {
      if (undone) return;
      try { await api.delete(`/${item._kind}s/${item.id}`); }
      catch { toast.error("Failed to delete — restored"); load(); }
    }, 5100);
  };

  const allItems = useMemo(() => {
    return tab === "photos"
      ? photos.map((x) => ({ ...x, _kind: "photo" }))
      : tab === "videos"
        ? videos.map((x) => ({ ...x, _kind: "video" }))
        : [
            ...videos.map((x) => ({ ...x, _kind: "video" })),
            ...photos.map((x) => ({ ...x, _kind: "photo" })),
          ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  }, [tab, photos, videos]);

  const visible = useMemo(() => {
    if (!activeFolder) return allItems;
    if (activeFolder === "unfiled") return allItems.filter((x) => !x.folder_id);
    return allItems.filter((x) => x.folder_id === activeFolder);
  }, [allItems, activeFolder]);

  const folderCounts = useMemo(() => {
    const counts = {};
    let unfiled = 0;
    for (const it of allItems) {
      if (it.folder_id) counts[it.folder_id] = (counts[it.folder_id] || 0) + 1;
      else unfiled++;
    }
    return { counts, unfiled };
  }, [allItems]);

  const openPicker = () => {
    if (tab === "videos") videoInputRef.current?.click();
    else photoInputRef.current?.click();
  };

  // Contextual FAB event: open the media file picker
  useEffect(() => {
    window.addEventListener("impo:media-upload", openPicker);
    return () => window.removeEventListener("impo:media-upload", openPicker);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  return (
    <>
      <TopBar
        title="Media"
        subtitle="Photos & videos — all your memories, one place"
        back
        showSearch
        right={
          <button
            onClick={openPicker}
            className="h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center"
            data-testid="media-add-button"
            aria-label="Add media"
          >
            <Plus className="h-4 w-4" />
          </button>
        }
      />
      <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={onPickPhoto} data-testid="media-photo-input" />
      <input ref={videoInputRef} type="file" accept="video/*" className="hidden" onChange={onPickVideo} data-testid="media-video-input" />

      <div className="px-4 pt-3">
        <div className="flex rounded-xl border border-border bg-card p-1" role="tablist" aria-label="Media filter">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              role="tab"
              aria-selected={tab === key}
              onClick={() => selectTab(key)}
              className={cn(
                "flex-1 h-9 rounded-lg text-xs font-medium inline-flex items-center justify-center gap-1.5 transition",
                tab === key ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              )}
              data-testid={`media-tab-${key}`}
            >
              <Icon className="h-3.5 w-3.5" /> {label}
              <span className="text-[10px] opacity-75">
                {key === "all" ? photos.length + videos.length : key === "photos" ? photos.length : videos.length}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Folder chips row (audit fix: media folders visible in Library) */}
      {(folders.length > 0 || folderCounts.unfiled > 0) && (
        <div className="px-4 pt-3">
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="media-folders-row">
            <button
              type="button"
              onClick={() => setActiveFolder(null)}
              className={cn(
                "shrink-0 rounded-full px-3 py-1.5 text-[11px] border transition inline-flex items-center gap-1.5",
                activeFolder === null
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card border-border text-muted-foreground hover:bg-[hsl(var(--accent))]"
              )}
              data-testid="media-folder-chip-all"
            >
              <Images className="h-3 w-3" /> All <span className="opacity-70">{allItems.length}</span>
            </button>
            {folders.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setActiveFolder(f.id)}
                className={cn(
                  "shrink-0 rounded-full px-3 py-1.5 text-[11px] border transition inline-flex items-center gap-1.5",
                  activeFolder === f.id
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card border-border text-muted-foreground hover:bg-[hsl(var(--accent))]"
                )}
                data-testid={`media-folder-chip-${f.id}`}
              >
                {activeFolder === f.id ? <FolderOpen className="h-3 w-3" /> : <Folder className="h-3 w-3" />}
                {f.name} <span className="opacity-70">{folderCounts.counts[f.id] || 0}</span>
              </button>
            ))}
            {folderCounts.unfiled > 0 && (
              <button
                type="button"
                onClick={() => setActiveFolder("unfiled")}
                className={cn(
                  "shrink-0 rounded-full px-3 py-1.5 text-[11px] border transition inline-flex items-center gap-1.5",
                  activeFolder === "unfiled"
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card border-border text-muted-foreground hover:bg-[hsl(var(--accent))]"
                )}
                data-testid="media-folder-chip-unfiled"
              >
                Unfiled <span className="opacity-70">{folderCounts.unfiled}</span>
              </button>
            )}
          </div>
        </div>
      )}

      <div className="px-4 py-4">
        {visible.length === 0 && !uploading && (
          <EmptyState
            icon={Images}
            title={tab === "videos" ? "No videos yet" : tab === "photos" ? "No photos yet" : "No memories yet"}
            subtitle="Save family moments, receipts, tutorials — tap the + above."
          />
        )}

        <div className="grid grid-cols-2 gap-3">
          {uploading && <div className="aspect-square rounded-2xl impo-shimmer" />}
          {visible.map((it) => {
            const isVideo = it._kind === "video";
            return (
              <div key={`${it._kind}-${it.id}`} className="relative rounded-2xl overflow-hidden border border-border bg-card" data-testid={`media-item-${it._kind}`}>
                <button
                  onClick={() => setPreview(it)}
                  className={cn(
                    "block w-full aspect-square relative",
                    isVideo && "bg-black/90 flex items-center justify-center"
                  )}
                >
                  {isVideo ? (
                    <>
                      <video
                        src={fileUrl(it.file_url)}
                        preload="metadata"
                        className="absolute inset-0 w-full h-full object-cover opacity-70"
                        muted
                        playsInline
                      />
                      <Play className="relative h-8 w-8 text-white/95 drop-shadow" />
                    </>
                  ) : (
                    <img src={fileUrl(it.file_url)} alt={it.title} className="w-full h-full object-cover" />
                  )}
                  <span className={cn(
                    "absolute top-2 left-2 rounded-full px-2 py-0.5 text-[10px] font-medium backdrop-blur",
                    isVideo ? "bg-black/60 text-white" : "bg-white/80 text-foreground"
                  )}>
                    {isVideo ? "Video" : "Photo"}
                  </span>
                </button>
                <div className="p-2">
                  <div className="text-xs font-medium truncate">{it.title}</div>
                  {isVideo && it.size_bytes && (
                    <div className="text-[10px] text-muted-foreground">{fmtSize(it.size_bytes)}</div>
                  )}
                </div>
                <button
                  onClick={() => del(it)}
                  className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"
                  data-testid={`media-delete-${it._kind}-button`}
                  aria-label={`Delete ${it._kind}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
                <div className="absolute bottom-11 right-2">
                  <FavPinButtons
                    type={it._kind}
                    id={it.id}
                    isFavorite={!!it.is_favorite}
                    pinnedAt={it.pinned_at}
                    size="sm"
                    variant="overlay"
                    onChanged={(u) => {
                      if (it._kind === "photo") setPhotos((arr) => arr.map((x) => x.id === it.id ? { ...x, ...u } : x));
                      else setVideos((arr) => arr.map((x) => x.id === it.id ? { ...x, ...u } : x));
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="rounded-2xl max-w-[92vw] md:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display truncate">{preview?.title}</DialogTitle>
          </DialogHeader>
          {preview?._kind === "video" ? (
            <video src={fileUrl(preview.file_url)} controls playsInline className="w-full rounded-xl bg-black" />
          ) : preview ? (
            <img src={fileUrl(preview.file_url)} alt="" className="w-full rounded-xl" />
          ) : null}
          {preview?.caption && <div className="text-sm text-muted-foreground mt-2">{preview.caption}</div>}
        </DialogContent>
      </Dialog>

      {/* Folder picker on upload */}
      <Dialog open={folderPicker.open} onOpenChange={(v) => { if (!v) setFolderPicker({ open: false, kind: null, file: null }); }}>
        <DialogContent className="rounded-2xl max-w-[92vw] md:max-w-md" data-testid="media-folder-picker">
          <DialogHeader>
            <DialogTitle className="font-display">Save to folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">Choose a folder for this {folderPicker.kind}, or save without one.</div>
            <div className="space-y-2">
              <label className={cn("flex items-center gap-2 p-3 rounded-xl border cursor-pointer", pickerChoice.mode === "none" ? "border-primary bg-primary/5" : "border-border")}>
                <input type="radio" name="folder-mode" checked={pickerChoice.mode === "none"} onChange={() => setPickerChoice({ mode: "none", id: "", name: "" })} className="accent-primary" />
                <span className="text-sm">No folder (Unfiled)</span>
              </label>
              {folders.length > 0 && (
                <label className={cn("flex items-start gap-2 p-3 rounded-xl border cursor-pointer", pickerChoice.mode === "existing" ? "border-primary bg-primary/5" : "border-border")}>
                  <input type="radio" name="folder-mode" checked={pickerChoice.mode === "existing"} onChange={() => setPickerChoice({ mode: "existing", id: folders[0].id, name: "" })} className="accent-primary mt-1" />
                  <div className="flex-1">
                    <div className="text-sm mb-2">Existing folder</div>
                    <select
                      className="w-full h-9 rounded-lg bg-background border border-border px-2 text-sm"
                      disabled={pickerChoice.mode !== "existing"}
                      value={pickerChoice.id}
                      onChange={(e) => setPickerChoice({ mode: "existing", id: e.target.value, name: "" })}
                      data-testid="media-folder-existing-select"
                    >
                      {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                    </select>
                  </div>
                </label>
              )}
              <label className={cn("flex items-start gap-2 p-3 rounded-xl border cursor-pointer", pickerChoice.mode === "new" ? "border-primary bg-primary/5" : "border-border")}>
                <input type="radio" name="folder-mode" checked={pickerChoice.mode === "new"} onChange={() => setPickerChoice({ mode: "new", id: "", name: "" })} className="accent-primary mt-1" />
                <div className="flex-1">
                  <div className="text-sm mb-2">Create new folder</div>
                  <Input
                    placeholder="e.g. Goa Trip 2026"
                    disabled={pickerChoice.mode !== "new"}
                    value={pickerChoice.name}
                    onChange={(e) => setPickerChoice({ mode: "new", id: "", name: e.target.value })}
                    className="h-9"
                    data-testid="media-folder-new-input"
                  />
                </div>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFolderPicker({ open: false, kind: null, file: null })}>Cancel</Button>
            <Button
              onClick={() => doUpload(folderPicker.file?.name)}
              disabled={pickerChoice.mode === "new" && !pickerChoice.name.trim()}
              data-testid="media-folder-picker-save"
            >
              Save {folderPicker.kind}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
